import { Component, EventEmitter, Inject, Input, Output } from '@angular/core';

import { HttpErrorResponse } from "@angular/common/http";
import { Observable, Subject, combineLatest, forkJoin, of, BehaviorSubject } from 'rxjs';
import { filter, map, mergeMap } from 'rxjs/operators';

import { MatDialog } from '@angular/material/dialog';
import { MessageDialogComponent } from '../../../../shared/dialogs/components/message-dialog.component';

import { applicationConstants, couleurIndicateurColors, etatOuvrageColors } from '../../../../app.constants';

import { MandatoryMap } from '../../../../shared/helpers/mandatory-map.class';
import { SubjectRelay } from '../../../../shared/commands/subject-relay.class';
import { DateService } from '../../../../shared/date-service/date.service';
import { LocalDateTime } from '../../../../shared/jsr310-localdates/types/local-date-time.type';

import { JSR310ConversionService } from '../../../../shared/jsr310-localdates/services/jsr310-conversion.service';
import { LocalDate } from '../../../../shared/jsr310-localdates/types/local-date.type';
import { LocalTime } from '../../../../shared/jsr310-localdates/types/local-time.type';

import { AccesService } from '../services/acces.service';
import { AccesTLR } from '../entities/acces-tlr.entity';
import { AccesOperationTLR } from '../entities/acces-operation-tlr.entity';
import { EtatOuvrage } from '../entities/etat-ouvrage.entity';
import { FermetureAccesOrder } from '../entities/fermeture-acces-order.entity';
import { OuvrageAccesDTO } from '../entities/ouvrage-acces-dto.entity';
import { DemandeFermetureValidationOrder } from '../entities/demande-fermeture-validation-order';
import { NiveauAlerteIncendie } from '../../../../static-types/entities/niveau-alerte-incendie.entity';

import { ArretSlbTLR } from '../../arret-slb/entities/arret-slb-tlr.entity';

import { ActeOnMService } from '../../../actes-onm/services/actes-onm.service';
import { ActeOnMTLR } from '../../../actes-onm/entities/acte-onm-tlr.entity';

import { MemoService } from '../../../memo/services/memo.service';
import { Memo } from '../../../memo/entities/memo.entity';

import { ClasseOuvrage } from '../../../structure/ouvrage/entities/classe-ouvrage.entity';
import { OuvrageService } from '../../../structure/ouvrage/services/ouvrage.service';
import { OuvrageTLR } from '../../../structure/ouvrage/entities/ouvrage-tlr.entity';

import { ParcService } from '../../../structure/parc/services/parc.service';
import { ParcTLR } from '../../../structure/parc/entities/parc-tlr.entity';

import { UserService } from '../../../humain/user/services/user.service';
import { UserTLR } from '../../../humain/user/entities/user-tlr.entity';

import { StaticTypeService } from '../../../../static-types/services/static-types.service';

import { EventBusService, Events } from '../../../../technical-modules/events/services/event-bus.service';

type DisplayedOperation = {
	entity: AccesOperationTLR,
	label: string,
	etat: string,
	acte: string,
	ouvrage: OuvrageTLR,
}

type DisplayedIndicateur = {
	nom: string,
	icon: string,
	couleur: string,
	niveau: string,
	regle: string | null,
	dateModification: LocalDateTime | null | undefined;
}

type DisplayedMemo = {
	entity: Memo,
	categorie: string,
}

type RadioOption<T> = {
	value: T,
	label: string,
	color?: string,
	title?: string,
}

@Component({
	selector: 'acces-fermeture',
	templateUrl: 'acces-fermeture.html',
	styleUrls: ['acces-fermeture.css'],
}) 
export class AccesFermetureComponent {
	readonly etatOuvrageColors = etatOuvrageColors;
	
	// Gestion globale de la fenetre
	displaySubject = new Subject<boolean>();
	allowsOuvragesConcernes: boolean = false;
	interAssocieesChapoDisplay = false;
	interAssocieeDisplay: boolean[] = [];
	shouldDisableButtons = false;

	classeSOLId!: number;
	classeONDId!: number;
	
	shouldSignalAbsenceArret: boolean = false;
	parc$ = new BehaviorSubject<ParcTLR | null>(null);
	
	// Filtre special des ouvrages concernes
	// Si le parc est d'énergie 'Solaire'
	// - on ne propose pas l'ouvrage ayant un classe d'ouvrage SOL
	filterOuvragesConcernes!: (parc: ParcTLR, ouvrage: OuvrageTLR) => boolean;
	getOuvrageConcerneLabel = (ouvrage: OuvrageTLR) => ouvrage.nom + (ouvrage.numeroSerie ? ' (' + ouvrage.numeroSerie + ')' : '');

	// Donnees a lier sur la vue
	loaded = false;
	etatsOuvrageOptionsWT: RadioOption<string>[] = [
		{
			value: 'OK',
			label: 'OK',
			color: '#52b050',
			title: 'Machine en production',
		},
		{
			value: 'OKD - Dégradé',
			label: 'OKD - Dégradé',
			color: '#ffc000',
			title: 'Bridage (ne produit pas à pleine puissance)',
		},
		{
			value: 'KOA - En panne',
			label: 'KOA - En panne',
			color: '#ff0000',
			title: 'Panne',
		},
		{
			value: 'KOM - Arrêt technique',
			label: 'KOM - Arrêt technique',
			color: '#f769dd',
			title: "Doit rester à l'arrêt",
		},
		{
			value: 'KO - Arrêt externe',
			label: 'KO - Arrêt externe',
			color: '#89479a',
			title: "Machine à l'arrêt cause Tarif négatif, limitation Enedis, Bat, avifaune, etc.",
		},
	];
	etatsOuvrageOptionsNonWT: RadioOption<string>[] = [
		{
			value: 'OK',
			label: 'OK',
		},
		{
			value: 'NOK',
			label: 'NOK',
		},
		{
			value: 'Mode dégradé',
			label: 'Mode dégradé',
		},
	];
	ouiNonOptions: RadioOption<boolean>[] = [
		{
			value: true,
			label: 'oui',
		},
		{
			value: false,
			label: 'non',
		},
	];
	
	acces!: AccesTLR;
	classeOuvrageWT!: ClasseOuvrage;
	parc!: ParcTLR;
	ouvrages!: OuvrageAccesDTO[];
	hasOuvragePorteFermee!: boolean;
	operations!: DisplayedOperation[];
	memosOnParc!: DisplayedMemo[];
	memosOnOuvrages!: DisplayedMemo[];
	
	dateMin!: LocalDate | null;
	
	// Saisies de l'utilisateur
	dateFin!: LocalDate | null;
	dateFinMax!: LocalDate | null;
	heureFin!: LocalTime | null;
	heureFinMax!: LocalTime;
	etatOuvrages!: string[];
	porteFermee!: boolean | null;
	selectedOuvragesConcernesIds!: number[];
	arretMachine!: boolean | null;
	commentaireArrets!: string;
	arretsAcces!: ArretSlbTLR[];
	fermeturesOperations!: boolean[];
	commentaireFermeture!: string;
	user!: UserTLR;
	demandeFermeture!: FermetureAccesOrder | null;
	niveauAlertIncendie: NiveauAlerteIncendie | null = null;
	indicateurs: DisplayedIndicateur[] = [];
	
	constructor(
		private dialog: MatDialog,
		@Inject(EventBusService) private eventBusService: EventBusService, 
		@Inject(DateService) private dateService: DateService,
		@Inject(JSR310ConversionService) private conversionService: JSR310ConversionService,
		
		@Inject(AccesService) private accesService: AccesService,
		@Inject(ActeOnMService) acteOnMService: ActeOnMService,
		@Inject(MemoService) memoService: MemoService,
		@Inject(OuvrageService) ouvrageService: OuvrageService,
		@Inject(ParcService) parcService: ParcService,
		@Inject(UserService) userService: UserService,
		
		@Inject(StaticTypeService) staticTypeService: StaticTypeService,
	) {
		// Fonction qui contourne le probleme que les forkJoin de [] ne se resolvent jamais
		let safeForkJoin = <T>(obs: Observable<T>[]) => {
			return obs.length ? forkJoin(obs) : of([]);	
		}

		// Des le chargement du composant 
		// 1- Charger l'utilisateur courrant
		let user$ = userService.getCurrentUserTLR();

		// 2- Charger les actes pour acces a leurs noms
		let actesById$ = acteOnMService.getMapById().pipe(
			map(
				(actesMap) => new MandatoryMap(actesMap, "ActeOm")
			)
		);
		
		// 3 - Charger le classe d'ouvrage 'WT
		let classesOuvrages$ =  ouvrageService.getClasseOuvrageList();
		let classeOuvrageWT$ = classesOuvrages$.pipe(
			map(
				(classes) => classes[classes.findIndex(classe => classe.code === 'WT')]
			)
		);
		let classeOuvrageSOL$ = classesOuvrages$.pipe(
			map(
				(classes) => classes[classes.findIndex(classe => classe.code === 'SOL')]
			)
		);
		let classeOuvrageOND$ = classesOuvrages$.pipe(
			map(
				(classes) => classes[classes.findIndex(classe => classe.code === 'OND')]
			)
		);
		let classeOuvrageGPO$ = classesOuvrages$.pipe(
			map(
				(classes) => classes[classes.findIndex(classe => classe.code === 'GPO')]
			)
		);
		let classeOuvragePDL$ = classesOuvrages$.pipe(
		    map(
		        (classes) => classes[classes.findIndex(classe => classe.code === 'PDL')]
		    )
		);
		
		// 4 - Charger les classes et sous-classes de memos
		let memoCategories$ = staticTypeService.getMemoCategoryMapById().pipe(
			map(
				(mcMap) => new MandatoryMap(mcMap, "MemoCategory")
			)
		);
		let memoSubCategories$ = staticTypeService.getMemoSubCategoryMapById().pipe(
			map(
				(mcMap) => new MandatoryMap(mcMap, "MemoSubCategory")
			)
		);
		
		// Chargement des niveaux d'alerte incendie
		let niveauxAlerteIncendie$ = staticTypeService.getNiveauAlerteIncendieMapById().pipe(
			map(
				(values) => new MandatoryMap<number, NiveauAlerteIncendie>(values, "NiveauAlerteIncendie")
			)
		);

		// A la reception d'une demande d'affichage
		// Charger l'acces correspondant
		let acces$ = this.displayCommandsSubject.pipe(
			mergeMap(
				(accesId) => this.accesService.getById(accesId)
			)
		);

		// Une fois l'acces obtenu, charger toutes les donnees complementaires
		let accesData$ = combineLatest([acces$, classeOuvrageWT$]).pipe(
			mergeMap(
				([acces, classeOuvrageWT]) => {
					this.dateMin = this.conversionService.toNullableLocalDate(
						this.dateService.addMinutesToDate(
							this.conversionService.fromMandatoryLocalDateTime(acces.dateDebut), 
						5)
					)
				
					let demandeFermeture$ = accesService.getDemandeFermeture(acces.id);
					let ouvrages$ = accesService.getOuvrageDTOByAcces(acces.id);
					let ouvrageConcernes$ = accesService.getOuvrageConcernesByAcces(acces.id);
					
					let hasOuvrageWT$ = ouvrages$.pipe(
						map(
							(ouvrages) => ouvrages.findIndex($ => $.classeOuvrage === classeOuvrageWT.id) >= 0
						)
					);

					let parc$ = parcService.getParcTLRById(acces.parcId);
					
					let niveauAlertIncendie$ = combineLatest([niveauxAlerteIncendie$,parc$]).pipe(map(([niveaux, parc]) => {
						if (!parc || parc.niveauIncendie == null) {
					            return null;
					        }
					        return niveaux.get(parc.niveauIncendie);
					    })
					);
					
					let indicateurs$ = parc$.pipe(
						mergeMap(
							(parc) => parcService.getParcIndicateurs(parc.id)
						),
						map(
							(indicateurs) => indicateurs
								.filter($ => $.actif !== false)
								.map(
									($) => {
										let config = applicationConstants.indicateurs[$.indicateurCode];
										return {
											nom: config ? config.nom : $.indicateurNom,
											icon: config ? config.icon : 'fa-solid fa-circle-question',
											couleur: couleurIndicateurColors[$.couleur] ?? '#b0b0b0',
											niveau: $.couleur,
											regle: $.regle,
											dateModification: $.dateModification
										} as DisplayedIndicateur;
									}
								)
						)
					);

					let operations$ = accesService.getOperationsTLRByAcces(acces.id);
					let displayedOperations$ = combineLatest([operations$, actesById$]).pipe(
						// Recuperer les donnees complementaires de l'operation a partir du serveur
						mergeMap(
							(data) => {
								let operations = data[0];
								let actesById = data[1];

								return safeForkJoin(
									operations.map(
										(operation) => {
											let ouvrage$ = ouvrageService.getOuvrageTLRById(operation.ouvrageId);

											let actesChain: ActeOnMTLR[] = [];
											let nextActeId = operation.acteOnmId;
											while (nextActeId != null && nextActeId != undefined) {
												let acte = actesById.get(nextActeId);
												actesChain.push(acte);
												nextActeId = acte.parentId;
											}
											actesChain = actesChain.reverse();

											return forkJoin({
												operation: of(operation),
												ouvrage: ouvrage$,
												actesChain: of(actesChain),
											})
										}
									)
								)
							}
						),
						// Une fois tous les chargement de la base faits, realiser les pre-calculs pour les futurs bindings de la vue
						map(
							(operationsDatas) => operationsDatas.map(
								({
									operation,
									ouvrage,
									actesChain,
								}) => {
									let acteLabel = actesChain
										.map(acte => acte.parentId ? acte.libelle : acte.code)
										.join(" - ")
										;

									let operationLabel = operation.codeCarl + ' : ' + ouvrage.nom + ':' + acteLabel;

									let etatLabel = '';
									switch (operation.etat) {
										case 'AWAITINGREAL':
											etatLabel = 'Planifié';
											break;
										case 'INPROGRESS':
											etatLabel = 'En cours';
											break;
										default:
											etatLabel = operation.etat;
									}

									return {
										entity: operation,
										label: operationLabel,
										etat: etatLabel,
										acte: acteLabel,
										ouvrage: ouvrage,
									} as DisplayedOperation;
								}
							)
						)
					);

					let dateDebutParam = this.conversionService.toLocalDateParam2(acces.dateDebut);
					let memosOnParc$ = memoService.getMemosOnParcForDate(acces.parcId, dateDebutParam);
					let displayMemosOnParc$ = combineLatest([memosOnParc$, memoCategories$, memoSubCategories$]).pipe(
						map(
							(data) => {
								let memos = data[0];
								let memoCategories = data[1];
								let memoSubCategories = data[2];
								
								return memos.map(
									(memo) => {
										let categorie = '';

										if (memo.categorieId) {
											categorie = memoCategories.get(memo.categorieId).label;
											if (memo.sousCategorieId) {
												categorie = categorie + ' (' + memoSubCategories.get(memo.sousCategorieId).label + ') '
											}
											categorie += ' - ';
										}
										
										return {
											entity: memo,
											categorie: categorie,	
										} as DisplayedMemo;
									}
								)								
							}
						)
					);
					let memosOnOuvrages$ = ouvrages$.pipe(
						mergeMap(
							(ouvrages) => {
								let ouvragesParam = ouvrages.map($ => $.id).join(",");
								return memoService.getMemosOnOuvragesForDate(ouvragesParam, dateDebutParam);
							}
						)
					);
					let displayMemosOnOuvrages$ = combineLatest([memosOnOuvrages$, memoCategories$, memoSubCategories$]).pipe(
						map(
							(data) => {
								let memos = data[0];
								let memoCategories = data[1];
								let memoSubCategories = data[2];
								
								return memos.map(
									(memo) => {
										let categorie = '';

										if (memo.categorieId) {
											categorie = memoCategories.get(memo.categorieId).label;
											if (memo.sousCategorieId) {
												categorie = categorie + ' (' + memoSubCategories.get(memo.sousCategorieId).label + ') '
											}
											categorie += ' - ';
										}
										
										return {
											entity: memo,
											categorie: categorie,	
										} as DisplayedMemo;
									}
								)								
							}
						)
					);

					return forkJoin({
						acces: of(acces),
						demandeFermeture: demandeFermeture$,
						classeOuvrageWT: of(classeOuvrageWT),
						parc: parc$,
						niveauAlertIncendie: niveauAlertIncendie$,
						ouvrages: ouvrages$,
						ouvragesConcernes: ouvrageConcernes$,
						hasOuvrageWT: hasOuvrageWT$,
						operations: displayedOperations$,
						memosOnParc: displayMemosOnParc$,
						memosOnOuvrages: displayMemosOnOuvrages$,
						indicateurs: indicateurs$,
					});
				}
			)
		);

		// Uniquement quand tous les elements sont charges, on bind les donnees sur les vue
		combineLatest([user$, classeOuvrageSOL$, classeOuvrageOND$, classeOuvrageGPO$, classeOuvragePDL$, accesData$]).subscribe(
			([user, classeOuvrageSOL, classeOuvrageOND, classeOuvrageGPO, classeOuvragePDL, accesData]) => {
				this.user = user;
				this.shouldSignalAbsenceArret = false;
				this.classeSOLId = classeOuvrageSOL.id;
				this.classeONDId = classeOuvrageOND.id;
				
				// Evol 52295, le filtre d'ouvrage concerne depend du type d'acces
				let parc = accesData.parc;
				if (parc.energie != 'Solaire') {
					// Pour les parcs eoliens, on ne peux pas selectionner d'ouvrages
					this.allowsOuvragesConcernes = false;
					this.filterOuvragesConcernes = (_parc: ParcTLR, _ouvrage: OuvrageTLR) => false; 
				} else {
					// Pour les parcs solaires, cela depends du type d'acces
					let isAccesLocal = (accesData.acces.typeAcces === 0);
					if (isAccesLocal) {
						// En acces local, tous les ouvrages sont selectionnes comme ouvrages concernes
						// sauf l'ouvrage SOL
						this.allowsOuvragesConcernes = true;
						this.filterOuvragesConcernes = (_parc: ParcTLR, ouvrage: OuvrageTLR) => ouvrage.classeOuvrage !== this.classeSOLId;
					} else {
						// En acces distant, seuls les ouvrages associes aux GPO de l'acces sont selectionnables
						let classeGPOId = classeOuvrageGPO.id;
						let ouvragesGPO = accesData.ouvrages.filter($ => $.classeOuvrage === classeGPOId);
						if (ouvragesGPO.length === 0) {
							// S'il n'y en a pas, ne pas afficher le champ
							this.allowsOuvragesConcernes = false;
							this.filterOuvragesConcernes = (_parc: ParcTLR, _ouvrage: OuvrageTLR) => false; 
						} else {
							let ouvragesGPOIds = ouvragesGPO.map($ => $.id);
							this.allowsOuvragesConcernes = true;
							this.filterOuvragesConcernes = (_parc: ParcTLR, ouvrage: OuvrageTLR) => {
								return ouvragesGPOIds.some($ => $ === ouvrage.ouvrageElecPere);
							}
						}
					}
				}

				this.acces = accesData.acces;
				this.demandeFermeture = accesData.demandeFermeture;
				this.classeOuvrageWT = accesData.classeOuvrageWT;
				this.parc = parc;
				this.niveauAlertIncendie = accesData.niveauAlertIncendie;
				this.indicateurs = accesData.indicateurs;
				this.ouvrages = accesData.ouvrages.sort((a, b) => a.nom.localeCompare(b.nom));
				if (parc.energie === 'Eolienne') {
				    this.hasOuvragePorteFermee = accesData.ouvrages.some($ => $.classeOuvrage === accesData.classeOuvrageWT.id|| $.classeOuvrage === classeOuvragePDL.id);
				} else if (parc.energie === 'Solaire') {
				    this.hasOuvragePorteFermee = accesData.ouvrages.some($ => $.classeOuvrage === classeOuvrageSOL.id);
				} else {
				    this.hasOuvragePorteFermee = false;
				}
				this.commentaireArrets = "";
				this.arretsAcces = [];
				this.operations = accesData.operations.sort((a, b) => a.entity.id - b.entity.id);
				this.memosOnParc = accesData.memosOnParc.sort((a,b) => a.entity.niveauId - b.entity.niveauId),
				this.memosOnOuvrages = accesData.memosOnOuvrages.sort((a,b) => a.entity.niveauId - b.entity.niveauId);
				// recupérer des ouvrages concernés s'il y en a
				this.selectedOuvragesConcernesIds = [];
				if (accesData.ouvragesConcernes) {
					this.selectedOuvragesConcernesIds = accesData.ouvragesConcernes.map($ => $.id);
				}

				// Initialiser les champs de saisie
							
				// Evol 54010: On ne fait plus l'arrondi aux 5mn les plus proches
				// let now = dateService.getCurrentRoundedDateAndTime();
				let now = new Date();
				now.setSeconds(0);
				now.setMilliseconds(0);

				let nowAsLocalDate = this.conversionService.toMandatoryLocalDate(now);
				this.dateFinMax = [... nowAsLocalDate];
				let nowAsLocalTime = this.conversionService.toMandatoryLocalTime(now);

				if (this.demandeFermeture) {

					if (this.demandeFermeture.dateFin) {
						this.dateFin = [this.demandeFermeture.dateFin[0], this.demandeFermeture.dateFin[1], this.demandeFermeture.dateFin[2]];
						this.heureFin = [this.demandeFermeture.dateFin[3],this.demandeFermeture.dateFin[4],0] as LocalTime;

						if (this.conversionService.equalsLocalDate(nowAsLocalDate, this.dateFin)) {
							let currentRoundedDateAndTime = this.dateService.getCurrentRoundedDateAndTime();
							this.heureFinMax = this.conversionService.toMandatoryLocalTime(currentRoundedDateAndTime);
						} else {
							this.heureFinMax = [23, 59, 59] as LocalTime;
						}
					} else {
						this.dateFin = [... nowAsLocalDate];
						this.heureFin = [... nowAsLocalTime];
						this.heureFinMax = [... nowAsLocalTime];
					}

					// Etats ouvrages
					this.etatOuvrages = this.ouvrages.map($ => $.etatAcces);
					
					this.porteFermee = this.demandeFermeture.porteFermee;
					this.arretMachine = this.demandeFermeture.arretMachine;
					this.selectedOuvragesConcernesIds = this.demandeFermeture.ouvragesConcernes || [];
					this.arretsAcces = this.demandeFermeture.arrets || [];
					this.commentaireArrets = this.demandeFermeture.commentaireArrets || '';
					this.fermeturesOperations = this.operations.map(operation =>
						this.demandeFermeture!.codesCarlOperations?.includes(operation.entity.codeCarl) || false
					);
					this.commentaireFermeture = this.demandeFermeture.commentaireFermeture || '';
					this.shouldSignalAbsenceArret = this.demandeFermeture.shouldSignalAbsenceArret || false;

				} else {

					if (this.acces.dateFin) {
						this.dateFin = [
							this.acces.dateFin[0],
							this.acces.dateFin[1],
							this.acces.dateFin[2]
						];

						this.heureFin = [this.acces.dateFin[3],this.acces.dateFin[4],0] as LocalTime;

						if (this.conversionService.equalsLocalDate(nowAsLocalDate, this.dateFin)) {
							let currentRoundedDateAndTime = this.dateService.getCurrentRoundedDateAndTime();
							this.heureFinMax = this.conversionService.toMandatoryLocalTime(currentRoundedDateAndTime);
						} else {
							this.heureFinMax = [23, 59, 59] as LocalTime;
						}
					} else {
						this.dateFin = [... nowAsLocalDate];
						this.heureFin = [... nowAsLocalTime];
						this.heureFinMax = [... nowAsLocalTime];
					}

					this.etatOuvrages = this.acces.etat === 2 ? new Array(this.ouvrages.length).fill(null) : this.ouvrages.map($ => $.etatAcces);
					this.porteFermee = this.acces.etat === 2 ? null : this.acces.porteFermee;
					this.arretMachine = this.acces.etat === 2 ? null : (this.acces.arretMachine === 1);
					this.fermeturesOperations = new Array(this.operations.length).fill(false);
					let commentParts = [ accesData.acces.commentaire, accesData.acces.commentaireFermeture ];
					this.commentaireFermeture = commentParts.filter($ => $ && $.length > 0).join("\n");
				} 
				this.loaded = true;
				this.displaySubject.next(true);
			}
		)

		// Quand on masque la fenetre de dialogue, on nettoie les donnees passees
		this.displaySubject.pipe(
			filter(show => !show)
		).subscribe(
			(_show) => {
				this.loaded = false;
			}
		)
	}

	@Input() id = 'accesFermeturePopup';

	// Liaison de l'observable servant a commander l'affichage du panneau
	private displayCommandsSubject = new SubjectRelay<number>();
	@Input() set displayCommands(obs: Observable<number> | undefined) {
		this.displayCommandsSubject.registerSource(obs);
	}
	
	@Output() closedAccesSaved = new EventEmitter<[AccesTLR, string]>();
	
	save(extraActionAfterClose: string) {
		// Validation de la saisie utilisateur
		if (!this.validateInputs()) {
			return;
		}
			
		let command = this.buildCommandObject();
		this.shouldDisableButtons = true;
		this.accesService.updateForFermeture(command).subscribe(
			(_id) => {
				this.shouldDisableButtons = false;
				this.displaySubject.next(false);

				if (extraActionAfterClose === 'duplicate') {
					this.eventBusService.emit(Events.DuplicateAccesModalRequested, this.acces.id);
				} else if (extraActionAfterClose === 'create_memo') {
					this.eventBusService.emit(Events.CreateMemoModalRequested, this.acces.id);
				}

				let message = this.acces.numero ? "L'accès " + this.acces.numero + " a été fermé avec succès" : "L'accès a été fermé avec succès";
				this.closedAccesSaved.emit([this.acces, message]);
			},
			(httpResponse: HttpErrorResponse) => {
				this.shouldDisableButtons = false;
				let errMsg =  (httpResponse && httpResponse.error && httpResponse.error['message']) ?
					httpResponse.error['message']:
					'Le serveur a rencontré une erreur imprévue'
				;
				this.dialog.open(MessageDialogComponent, {
					width: '400px',
					data: {
						title: 'Enregistrement impossible',
						messages: [errMsg]
					}
				});
				console.error("Erreur lors la tentivate d'enregistrement de l'acces #" + this.acces.id + " au statut ferme", httpResponse);
			}
		);
	}
	
	private validateInputs(): boolean {
		let errors: string[] = [];
		
		if (!this.dateFin) {
			errors.push("Veuillez renseigner l'heure de fin.");
		}
		
		if (this.etatOuvrages.findIndex($ => $ === null) > -1) {
			if (this.ouvrages.length > 1) {
				errors.push("Veuillez renseigner l'état de l'ouvrage.");
			} else {
				errors.push("Veuillez renseigner l'état de tous les ouvrages.");
			}
		}
		
		if (this.hasOuvragePorteFermee && this.acces.typeAcces === 0 && this.porteFermee === null) { 
			errors.push("Veuillez indiquer si la porte est fermée.");
		}
		
		if (this.arretMachine === null) {
			errors.push("Veuillez indiquer si l'accès a donné lieu à un arrêt machine.");
		}
		
		// Pas de controle a faire
		if (errors.length > 0) {
			this.dialog.open(MessageDialogComponent, {
				width: '400px',
				data: {
					title: 'Enregistrement impossible',
					messages: errors
				}
			});

			return false;
		}
		
		return true;
	}

	private buildCommandObject(): FermetureAccesOrder {
		let accesId = this.acces.id;
		
		let nullSafe = <T> (value: T | null) =>  {
			if (value === null)
				throw Error("Null n'est pas cense etre une valeur possible apres la validation");
				
			return value;
		}
		let dateHeureFin = this.conversionService.merge(nullSafe(this.dateFin), nullSafe(this.heureFin));
		if (this.acces.ice) {
			// la date de fin doit être convertie en UTC
			const browserDateFin = new Date(dateHeureFin[0], dateHeureFin[1] - 1,
	            dateHeureFin[2], dateHeureFin[3],
	            dateHeureFin[4], dateHeureFin[5]
			);
			let utcDateFin = new Date(browserDateFin.setMinutes(browserDateFin.getMinutes() + browserDateFin.getTimezoneOffset()));
			let utcConvertedDate = this.conversionService.toLocalDateTime(utcDateFin);
			if (utcConvertedDate) {
				dateHeureFin = utcConvertedDate;
			}
		}
		
		let etats: EtatOuvrage[] = [];
		for (let i = 0; i < this.ouvrages.length; i++) {
			etats.push(new EtatOuvrage(this.ouvrages[i].id, this.etatOuvrages[i]));
		}
		
		let extractSelectedEntities = <T> (objects: T[], selected: boolean[]) => {
			let entities: T[] = [];
			for (let i = 0; i < objects.length; i++) {
				if (selected[i]) {
					entities.push(objects[i]);
				}
			}
			return entities;
		}
		let operationsSelectionees = extractSelectedEntities(this.operations, this.fermeturesOperations);
		let operationsSelectioneesCodesCarl = operationsSelectionees.map($ => $.entity.codeCarl);
		
		let commandObject = new FermetureAccesOrder(
			accesId,
			dateHeureFin,
			etats,
			this.hasOuvragePorteFermee && this.acces.typeAcces === 0 ? nullSafe(this.porteFermee) : false,
			this.selectedOuvragesConcernesIds,
			this.arretMachine,
			this.arretsAcces,
			this.commentaireArrets,
			operationsSelectioneesCodesCarl,
			this.commentaireFermeture,
			this.shouldSignalAbsenceArret,
		);
		
		return commandObject;
	}
	
	close() {
		this.displaySubject.next(false);
	}
	
	dateFinHasChanged(dateFin: LocalDate | null) {
		console.log('dateFinhasChanged: ', dateFin);
		let now = this.conversionService.toMandatoryLocalDate(new Date());
		if (this.conversionService.equalsLocalDate(now, dateFin)) {
			let currentRoundedDateAndTime = this.dateService.getCurrentRoundedDateAndTime();
			this.heureFinMax = this.conversionService.toMandatoryLocalTime(currentRoundedDateAndTime);			
		} else {
			this.heureFinMax = [23, 59, 59] as LocalTime;
		}
		this.dateFinMax = [... now];
		console.log('dateFinhasChanged, new heureFinMax: ', this.heureFinMax);
	}

	signalerNoArretsFound(value: boolean){
		this.shouldSignalAbsenceArret = value;
	}
	
	demanderFermeture() {
		// Validation de la saisie utilisateur
		if (!this.validateInputs()) {
			return;
		}

		let command = this.buildCommandObject();

		this.shouldDisableButtons = true;

		this.accesService.demanderFermeture(command).subscribe(
			(_id) => {
				this.shouldDisableButtons = false;
				this.displaySubject.next(false);

				let message = this.acces.numero
					? "La demande de fermeture pour l'accès " + this.acces.numero + " a été envoyée avec succès"
					: "La demande de fermeture a été envoyée avec succès";

				this.closedAccesSaved.emit([this.acces, message]);
			},
			(httpResponse: HttpErrorResponse) => {
				this.shouldDisableButtons = false;

				let errMsg = (httpResponse && httpResponse.error && httpResponse.error['message'])
					? httpResponse.error['message']
					: 'Le serveur a rencontré une erreur imprévue';

				this.dialog.open(MessageDialogComponent, {
					width: '400px',
					data: {
						title: 'Enregistrement impossible',
						messages: [errMsg]
					}
				});

				console.error(
					"Erreur lors de la demande de fermeture de l'acces #" + this.acces.id,
					httpResponse
				);
			}
		);
	}
	
	annulerFermeture() {
		const now = this.conversionService.toLocalDateTime(new Date());
		const command = new DemandeFermetureValidationOrder(
			this.acces.id,
			now
		);

		this.shouldDisableButtons = true;
		this.accesService.denyDemandeFermeture(command).subscribe(
			(_response) => {
				this.shouldDisableButtons = false;
				this.displaySubject.next(false);
				let message = this.acces.numero
					? "La demande de fermeture pour l'accès " + this.acces.numero + " a été annulée avec succès"
					: "La demande de fermeture a été annulée avec succès";
				this.closedAccesSaved.emit([this.acces, message]);
			},
			(httpResponse: HttpErrorResponse) => {
				this.shouldDisableButtons = false;
				let errMsg = (httpResponse && httpResponse.error && httpResponse.error['message'])
					? httpResponse.error['message']
					: 'Le serveur a rencontré une erreur imprévue';
				this.dialog.open(MessageDialogComponent, {
					width: '400px',
					data: {
						title: 'Annulation impossible',
						messages: [errMsg]
					}
				});

				console.error(
					"Erreur lors de l'annulation de la demande de fermeture de l'accès #" + this.acces.id,
					httpResponse
				);
			}
		);
	}
	
	get shouldDisplayPorteFermee(): boolean {
	    return this.hasOuvragePorteFermee && this.acces.typeAcces === 0;
	}
}